import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import inspectionPointLibraryService, {
  InspectionPointLibrary,
  InspectionPointFilters,
} from '../services/inspectionPointLibrary.service';

// Query keys
export const inspectionPointLibraryKeys = {
  all: ['inspection-point-library'] as const,
  lists: () => [...inspectionPointLibraryKeys.all, 'list'] as const,
  list: (filters?: InspectionPointFilters) => [...inspectionPointLibraryKeys.lists(), filters] as const,
  details: () => [...inspectionPointLibraryKeys.all, 'detail'] as const,
  detail: (id: string) => [...inspectionPointLibraryKeys.details(), id] as const,
  byCategory: (category: string) => [...inspectionPointLibraryKeys.all, 'category', category] as const,
};

// Hooks
export const useInspectionPointLibrary = (
  filters?: InspectionPointFilters
): UseQueryResult<InspectionPointLibrary[], Error> => {
  return useQuery({
    queryKey: inspectionPointLibraryKeys.list(filters),
    queryFn: () => inspectionPointLibraryService.getAll(filters),
  });
};

export const useInspectionPointsByCategory = (
  category: string
): UseQueryResult<InspectionPointLibrary[], Error> => {
  return useQuery({
    queryKey: inspectionPointLibraryKeys.byCategory(category),
    queryFn: () => inspectionPointLibraryService.getByCategory(category),
    enabled: !!category,
  });
};

export const useInspectionPoint = (id: string): UseQueryResult<InspectionPointLibrary, Error> => {
  return useQuery({
    queryKey: inspectionPointLibraryKeys.detail(id),
    queryFn: () => inspectionPointLibraryService.getById(id),
    enabled: !!id,
  });
};

export const useCreateInspectionPoint = (): UseMutationResult<
  InspectionPointLibrary,
  Error,
  Partial<InspectionPointLibrary>
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data) => inspectionPointLibraryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inspectionPointLibraryKeys.lists() });
      message.success('Inspection point created successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create inspection point');
    },
  });
};

export const useUpdateInspectionPoint = (): UseMutationResult<
  InspectionPointLibrary,
  Error,
  { id: string; data: Partial<InspectionPointLibrary> }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, data }) => inspectionPointLibraryService.update(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: inspectionPointLibraryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inspectionPointLibraryKeys.detail(variables.id) });
      message.success('Inspection point updated successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to update inspection point');
    },
  });
};

export const useDeleteInspectionPoint = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => inspectionPointLibraryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inspectionPointLibraryKeys.lists() });
      message.success('Inspection point deleted successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to delete inspection point');
    },
  });
};
